/**
 * 치료 방향 (A33): 대화를 근거로 여러 치료 접근에 근거를 쌓고, 가장 근거가 많은 접근을 추천한다.
 * 근거 종류 4가지 — 필요 신호 / 부합 / 해야 하는 근거 / 시행 흔적. 근거 하나가 +1.
 * 채택은 상담자가 한다. 채택하면 그 접근의 단계대로 진행하고, 끝나면 완료로 표시한다 (드물게 새 방향 시작).
 * 단계·기법 설명은 각 접근의 일반적인 진행 구조를 짧게 요약한 것이다 (상담자 안내용, 문서에 넣지 않음).
 */
import type { ApproachEvidence, ApproachEvidenceKind, ApproachId, TreatmentPlan } from '../schema/v1';

export type { ApproachId };
export type EvidenceKind = ApproachEvidenceKind;

export const EVIDENCE_KIND_LABEL: Record<EvidenceKind, string> = {
  need: '필요 신호',
  fit: '부합',
  indication: '해야 하는 근거',
  applied: '시행 흔적',
};

export type ApproachDef = {
  id: ApproachId;
  name: string;
  scholar: string;
  /** 다른 이름 (외부 AI 답을 읽을 때) */
  aliases: string[];
  /** 이 접근이 필요하다는 신호 (외부 AI에게 알려주는 판단 기준) */
  signals: string;
  /** 진행 단계와 단계마다 상담자가 할 일 */
  steps: { title: string; guide: string }[];
  /** 축어록 포인트에서 "치료 기법"으로 볼 상담자 말투 */
  technique: RegExp;
};

export const APPROACHES: ApproachDef[] = [
  {
    id: 'cbt',
    name: '인지행동치료',
    scholar: 'Aaron Beck',
    aliases: ['CBT', '인지치료', '인지 행동', '인지행동'],
    signals:
      '자동적 사고·인지 왜곡(과잉일반화, 흑백논리, 독심술 등), 생각이 감정·행동을 끌고 가는 패턴, 구조화·과제에 대한 수용',
    steps: [
      {
        title: '관계 형성과 사례개념화 공유',
        guide: '주호소를 생각-감정-행동의 고리로 함께 정리하고, 이 방식으로 진행하는 이유를 설명한다.',
      },
      { title: '심리교육', guide: '상황 → 자동적 사고 → 감정 → 행동의 연결을 내담자의 실제 예로 보여준다.' },
      {
        title: '자동적 사고 찾기·기록',
        guide: '감정이 크게 움직인 순간의 생각을 찾고, 사고 기록지를 과제로 시작한다.',
      },
      { title: '인지 재구성', guide: '그 생각의 근거와 반대 근거를 검토하고, 균형 잡힌 대안적 생각을 함께 만든다.' },
      {
        title: '행동 실험·행동 활성화',
        guide: '예측을 실제로 확인해 보는 작은 실험이나 회피하던 활동을 단계적으로 해 본다.',
      },
      { title: '핵심 신념 다루기', guide: '반복되는 자동적 사고 아래의 핵심 신념을 찾고 새로운 신념을 키운다.' },
      { title: '재발 방지·종결 준비', guide: '배운 것을 정리하고, 다시 어려워질 때 쓸 계획을 세운다.' },
    ],
    technique:
      /(그 생각이|어떤 생각이 (들|드)|자동적|생각.{0,6}(근거|증거)|다르게 (볼|생각해|보면)|다른 (설명|가능성)|최악의|실제로 (일어날|그럴) 가능성|기록해|적어 (보|오)|과제|행동 실험|해 보시면|연습해)/,
  },
  {
    id: 'eft',
    name: '정서중심치료',
    scholar: 'Leslie Greenberg',
    aliases: ['EFT', '정서 중심', '정서초점', '정서중심'],
    signals:
      '정서를 피하거나 눌러 둠, 정서를 말로 표현하기 어려움, 미해결 감정(특정 인물에 대한), 자기 비판적 내적 갈등',
    steps: [
      {
        title: '공감적 관계와 정서 탐색',
        guide: '내담자의 정서에 머물며 공감적으로 반영하고, 정서를 안전하게 느낄 수 있게 한다.',
      },
      { title: '정서 알아차림·명명', guide: '지금 몸과 마음에서 느껴지는 것을 함께 알아차리고 이름 붙인다.' },
      {
        title: '일차 정서 접근',
        guide: '표식(미해결 과제, 자기 분열)이 보이면 빈 의자·두 의자 작업으로 일차 정서에 닿는다.',
      },
      {
        title: '부적응 정서 변형',
        guide: '부적응 정서(수치심·두려움)를 새로운 적응적 정서(슬픔·자기 연민·주장)로 바꾼다.',
      },
      { title: '새로운 의미 구성', guide: '바뀐 정서 경험을 바탕으로 자기와 관계에 대한 새 이야기를 만든다.' },
    ],
    technique:
      /(지금 (느껴|어떤 느낌)|몸에서|그 감정에 (머물|조금 더)|빈 의자|의자에|그분께 (말|하고 싶)|마음 한쪽|두 마음|그 마음이 뭐라고)/,
  },
  {
    id: 'act',
    name: '수용전념치료',
    scholar: 'Steven Hayes',
    aliases: ['ACT', '수용 전념', '수용과 전념', '수용전념'],
    signals: '생각·감정과 싸우거나 피하려다 삶이 좁아짐, 생각에 휩쓸림(융합), 가치가 흐려짐',
    steps: [
      { title: '창조적 절망감', guide: '지금까지 통제하고 피하려던 노력이 어떤 대가를 치렀는지 함께 살핀다.' },
      { title: '수용', guide: '불편한 감정을 없애려 하지 않고 기꺼이 경험해 보는 연습을 한다.' },
      { title: '인지적 탈융합', guide: '"나는 ~라는 생각을 하고 있다"처럼 생각과 거리를 두는 연습을 한다.' },
      { title: '현재 순간과 관찰하는 자기', guide: '지금 이 순간을 알아차리고, 생각·감정을 바라보는 자리를 경험한다.' },
      { title: '가치 명료화', guide: '내담자에게 정말 중요한 방향(가치)을 분명히 한다.' },
      { title: '전념 행동', guide: '가치에 맞는 작은 행동을 정하고 실천한다.' },
    ],
    technique:
      /(생각이 (든다|떠오른다)는 것을|그 생각을 (붙잡|흘려|바라)|지금 이 순간|가치|정말 중요한|그럼에도|기꺼이|알아차려 보)/,
  },
  {
    id: 'pct',
    name: '인간중심상담',
    scholar: 'Carl Rogers',
    aliases: ['PCT', '인간 중심', '내담자 중심', '인간중심'],
    signals: '자기 수용이 낮음, 타인의 기준에 맞춰 사는 자기, 안전한 관계 경험 자체가 필요함',
    steps: [
      { title: '관계 형성', guide: '공감적 이해·무조건적 긍정적 존중·진실성으로 안전한 관계를 만든다.' },
      { title: '자기 탐색 촉진', guide: '판단 없이 내담자의 경험을 따라가며 반영한다.' },
      { title: '자기 개념과 경험의 불일치 다루기', guide: '"이래야 한다"는 자기와 실제 경험 사이의 간극을 함께 본다.' },
      { title: '자기 수용과 성장', guide: '내담자가 자기 경험을 신뢰하고 스스로 방향을 찾도록 돕는다.' },
    ],
    technique: /(그렇게 느끼셨|마음이셨|그런 마음이 드는 게|충분히 그럴 수|그대로 괜찮|말씀하신 걸 들으니)/,
  },
  {
    id: 'pdt',
    name: '정신역동·대상관계',
    scholar: 'Otto Kernberg · Heinz Kohut',
    aliases: ['정신역동', '대상관계', '정신분석', '자기심리학', 'psychodynamic'],
    signals: '어린 시절 관계와 지금 관계의 반복, 상담자와의 관계에서 드러나는 패턴(전이), 오래된 내적 갈등',
    steps: [
      { title: '치료 동맹', guide: '안정된 틀과 관계를 만든다.' },
      { title: '반복 패턴 탐색', guide: '지금 관계에서 반복되는 감정·기대·두려움을 찾는다.' },
      { title: '과거 관계와 연결', guide: '반복 패턴을 초기 관계 경험과 연결해 본다.' },
      { title: '전이 다루기', guide: '상담자와의 관계에서 나타나는 패턴을 지금 여기서 다룬다.' },
      { title: '통찰과 훈습', guide: '얻은 통찰을 여러 장면에서 반복해 확인하고 다지는 과정을 거친다.' },
    ],
    technique: /(어렸을 때|부모님과의|비슷한 느낌을 (예전|전에)|저와의 관계|지금 여기서 저에게|반복되는|그때와)/,
  },
  {
    id: 'sfbt',
    name: '해결중심상담',
    scholar: 'Steve de Shazer · Insoo Kim Berg',
    aliases: ['SFBT', '해결 중심', '해결중심'],
    signals: '문제보다 원하는 변화가 분명함, 이미 잘 대처한 예외가 있음, 짧은 기간의 상담이 필요함',
    steps: [
      { title: '목표 설정', guide: '기적 질문 등으로 원하는 모습을 구체적인 행동으로 그린다.' },
      { title: '예외 찾기', guide: '문제가 덜했던 때와 그때 한 것을 찾는다.' },
      { title: '척도 질문', guide: '지금 몇 점인지, 1점 올라가면 무엇이 달라지는지 묻는다.' },
      { title: '작은 변화 과제', guide: '이미 하고 있는 것을 조금 더 하는 과제를 정한다.' },
      { title: '강화와 종결', guide: '변화를 알아차리고 칭찬하며 스스로 이어가도록 한다.' },
    ],
    technique: /(기적이 일어|예외|그렇지 않았던 때|척도로|몇 점|1점만 올라|어떻게 해내셨|이미 하고 계신)/,
  },
  {
    id: 'narr',
    name: '내러티브상담',
    scholar: 'Michael White · David Epston',
    aliases: ['내러티브', 'narrative', '이야기치료'],
    signals: '문제가 곧 자기라고 여김("나는 원래 그런 사람"), 지배적인 문제 이야기, 사회·문화적 기대에 눌림',
    steps: [
      { title: '문제 이야기 듣기', guide: '문제가 삶에 끼친 영향을 충분히 듣는다.' },
      { title: '외재화', guide: '문제에 이름을 붙여 사람과 문제를 분리한다.' },
      { title: '독특한 결과 찾기', guide: '문제에 휘둘리지 않았던 순간을 찾는다.' },
      { title: '대안 이야기 쓰기', guide: '독특한 결과를 이어 새로운 이야기를 함께 쓴다.' },
    ],
    technique:
      /(그 (불안|문제|목소리)(이|가) .{0,8}(당신|내담자|님)|문제에 이름|그것이 .{0,6}(하게 만들|속삭)|독특한|다른 이야기)/,
  },
  {
    id: 'fam',
    name: '가족체계상담',
    scholar: 'Murray Bowen',
    aliases: ['보웬', '가족체계', '가족치료', 'family systems'],
    signals: '가족 안의 역할·삼각관계, 원가족과의 미분화, 가족 사이에서 정서가 쉽게 휩쓸림',
    steps: [
      { title: '가계도', guide: '세대에 걸친 관계와 패턴을 가계도로 그린다.' },
      { title: '분화 수준 탐색', guide: '감정과 생각을 구분하고 자기 입장을 지키는 정도를 살핀다.' },
      { title: '삼각관계 파악', guide: '긴장이 생길 때 누가 끌려 들어가는지 본다.' },
      { title: '정서적 반응성 낮추기', guide: '반사적인 반응 대신 생각하고 반응하는 연습을 한다.' },
      { title: '분화 향상', guide: '가족과 연결된 채로 자기 입장을 지키는 시도를 한다.' },
    ],
    technique: /(가계도|집안에서|가족 안에서 (역할|자리)|삼각|부모님 사이|가족 중에 (누가|비슷))/,
  },
  {
    id: 'att',
    name: '애착 기반 상담',
    scholar: 'John Bowlby',
    aliases: ['애착', 'attachment'],
    signals: '버려짐·거절에 대한 두려움, 가까워지면 불안하거나 거리를 둠, 의지와 자립 사이의 갈등',
    steps: [
      { title: '안전기지 형성', guide: '상담 관계를 안전기지로 경험하게 한다.' },
      { title: '애착 패턴 탐색', guide: '가까운 관계에서 반복되는 기대와 반응을 찾는다.' },
      { title: '교정적 정서 경험', guide: '상담 관계에서 다른 반응을 경험하게 한다.' },
      { title: '새로운 관계 방식 연습', guide: '가까운 관계에서 필요를 표현하는 연습을 한다.' },
    ],
    technique: /(버려질|떠날까 봐|가까워지면|의지하|안전하다고 느|기대고 싶)/,
  },
  {
    id: 'past',
    name: '목회상담(영성 통합)',
    scholar: 'Howard Clinebell',
    aliases: ['목회상담', '영성', '목회돌봄', '기독교상담', 'pastoral'],
    signals: '신앙이 고통의 자원이자 상처가 됨, 하나님 이미지와 자기 이미지의 연결, 신앙 공동체와의 관계 문제',
    steps: [
      { title: '신앙 이야기 경청', guide: '고통 속에서 신앙이 어떤 의미인지 판단 없이 듣는다.' },
      { title: '신앙 자원과 상처 탐색', guide: '신앙이 힘이 된 경험과 상처가 된 경험을 함께 본다.' },
      { title: '하나님 이미지 탐색', guide: '하나님 이미지가 자기 이미지·관계와 어떻게 연결되는지 살핀다.' },
      { title: '신앙과 삶의 통합', guide: '탄원·감사 등 신앙의 언어로 경험을 통합하도록 돕는다.' },
      { title: '공동체 연결', guide: '안전한 신앙 공동체 자원과 연결한다.' },
    ],
    technique: /(하나님|기도|말씀|신앙|공동체|은혜|성경)/,
  },
];

export const approachById = (id: string) => APPROACHES.find((a) => a.id === id);

/** 외부 AI 답의 접근 이름 → id */
export function approachIdOf(name: string): ApproachId | undefined {
  const n = name.replace(/\s+/g, '').toLowerCase();
  for (const a of APPROACHES) {
    if (a.id === n) return a.id;
    if ([a.name, ...a.aliases].some((x) => n.includes(x.replace(/\s+/g, '').toLowerCase()))) return a.id;
  }
  return undefined;
}

export type ApproachEvidenceItem = ApproachEvidence;

const KIND_ALIASES: [EvidenceKind, RegExp][] = [
  ['applied', /applied|시행|사용|적용/i],
  ['indication', /indication|해야|적응증|근거/i],
  ['need', /need|필요/i],
  ['fit', /fit|부합|맞/i],
];

/** 외부 AI 답의 근거 종류 → kind */
export function evidenceKindOf(v: string): EvidenceKind | undefined {
  const t = v.trim();
  if ((['need', 'fit', 'indication', 'applied'] as string[]).includes(t)) return t as EvidenceKind;
  return KIND_ALIASES.find(([, re]) => re.test(t))?.[0];
}

export type ApproachScore = {
  approach: ApproachDef;
  total: number;
  byKind: Record<EvidenceKind, number>;
  sessions: number;
};

/** 접근별 점수 (뺀 근거 제외), 높은 순 */
export function scoreApproaches(items: (ApproachEvidenceItem & { sessionNumber: number })[]): ApproachScore[] {
  const out: ApproachScore[] = [];
  for (const a of APPROACHES) {
    const mine = items.filter((x) => x.approach === a.id && !x.excluded);
    if (!mine.length) continue;
    const byKind: Record<EvidenceKind, number> = { need: 0, fit: 0, indication: 0, applied: 0 };
    for (const x of mine) byKind[x.kind]++;
    out.push({ approach: a, total: mine.length, byKind, sessions: new Set(mine.map((x) => x.sessionNumber)).size });
  }
  return out.sort((x, y) => y.total - x.total || y.sessions - x.sessions);
}

/**
 * 추천: 1위가 근거 4개 이상이고 2위보다 2개 이상 앞서며, 필요 신호와 해야 하는 근거가 모두 있을 때.
 * 아니면 무엇을 더 확인하면 좋을지 알려준다.
 */
export function recommendApproach(scores: ApproachScore[]): { approach?: ApproachDef; note: string } {
  const [a, b] = scores;
  if (!a) return { note: '아직 근거가 없습니다. 회기 분석을 반영하면 쌓입니다.' };
  const lead = a.total - (b?.total ?? 0);
  if (a.total >= 4 && lead >= 2 && a.byKind.need > 0 && a.byKind.indication > 0)
    return {
      approach: a.approach,
      note: `${a.approach.name}의 근거가 가장 많이 쌓였습니다 (${a.total}개, ${a.sessions}회기에 걸쳐).`,
    };
  const missing = (['need', 'indication', 'fit'] as EvidenceKind[])
    .filter((k) => a.byKind[k] === 0)
    .map((k) => EVIDENCE_KIND_LABEL[k]);
  return {
    note:
      lead < 2 && b
        ? `${a.approach.name}와(과) ${b.approach.name}이(가) 비슷합니다. 다음 회기에 두 접근을 구분할 근거를 더 확인해 보세요.`
        : `아직 판단하기 이릅니다${missing.length ? ` — ${a.approach.name}의 ${missing.join('·')}을(를) 더 확인해 보세요` : ''}.`,
  };
}

/** 외부 AI에게 보내는 접근 목록 (id · 이름 · 판단 기준) */
export function approachListText(): string {
  return APPROACHES.map((a) => `- ${a.id}: ${a.name} (${a.scholar}) — ${a.signals}`).join('\n');
}

/** 채택한 방향의 지금 단계 */
export function currentStepOf(plan: TreatmentPlan | undefined) {
  if (!plan) return undefined;
  const approach = approachById(plan.approach);
  if (!approach) return undefined;
  const index = Math.min(plan.currentStep, approach.steps.length);
  return {
    approach,
    index,
    total: approach.steps.length,
    step: approach.steps[index],
    allStepsDone: index >= approach.steps.length,
    completed: plan.status === 'completed',
  };
}

/** 보조 접근 (있으면) */
export function secondaryOf(plan: TreatmentPlan | undefined) {
  const s = plan?.secondary;
  const approach = s ? approachById(s.approach) : undefined;
  return approach && s ? { approach, role: s.role } : undefined;
}

/** 축어록 포인트의 "치료 기법": 주 접근과 보조 접근의 기법 말투를 함께 본다 */
export function techniquePattern(plan: TreatmentPlan | undefined): RegExp | undefined {
  const main = plan ? approachById(plan.approach) : undefined;
  if (!main) return undefined;
  const sec = secondaryOf(plan);
  return sec ? new RegExp(`${main.technique.source}|${sec.approach.technique.source}`) : main.technique;
}

/** 한 줄 표시: "인지행동치료 · 3/7단계 자동적 사고 찾기·기록" */
export function currentStepLabel(plan: TreatmentPlan | undefined): string | undefined {
  const c = currentStepOf(plan);
  if (!c) return undefined;
  if (c.completed) return `${c.approach.name} · 치료 완료`;
  if (c.allStepsDone) return `${c.approach.name} · 모든 단계를 마침`;
  return `${c.approach.name} · ${c.index + 1}/${c.total}단계 ${c.step!.title}`;
}

/**
 * 채택 뒤 다른 접근에 근거가 새로 쌓이는지 (참고 안내만, 방향을 바꾸라는 뜻이 아님).
 * 채택 뒤 회기의 근거 중 필요 신호·해야 하는 근거가 3개 이상인 다른 접근.
 */
export function growingOthers(scoresAfter: ApproachScore[], adopted: ApproachId): ApproachScore[] {
  return scoresAfter.filter((s) => s.approach.id !== adopted && s.byKind.need + s.byKind.indication >= 3);
}

/** 외부 AI 자료의 [치료 방향] 칸 */
export function treatmentPacketText(plan: TreatmentPlan | undefined, scores: ApproachScore[]): string {
  const c = currentStepOf(plan);
  if (c && !c.completed) {
    const next = c.approach.steps[c.index + 1];
    return [
      `채택한 접근: ${c.approach.name} (${c.approach.scholar}) — 이 접근을 주 이론으로 일관되게 진행한다.`,
      c.allStepsDone
        ? '지금 단계: 모든 단계를 마침 (종결 준비)'
        : `지금 단계: ${c.index + 1}/${c.total} ${c.step!.title} — ${c.step!.guide}`,
      next ? `다음 단계: ${next.title}` : '',
      secondaryOf(plan)
        ? `보조 접근: ${secondaryOf(plan)!.approach.name} (${secondaryOf(plan)!.approach.scholar})${secondaryOf(plan)!.role ? ` — 쓰는 자리: ${secondaryOf(plan)!.role}` : ''}. 주 이론의 틀 안에서 이 자리에만 쓴다.`
        : '',
    ]
      .filter(Boolean)
      .join('\n');
  }
  const top = scores.slice(0, 4);
  return [
    c?.completed
      ? `이전 방향 ${c.approach.name}은(는) 치료 완료로 표시됨. 새 방향은 아직 정하지 않음.`
      : '아직 채택한 접근 없음 (근거를 모으는 중).',
    top.length ? `지금까지 쌓인 근거: ${top.map((s) => `${s.approach.name} ${s.total}`).join(', ')}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * 새 AI 근거로 바꾸되, 상담자가 뺀 근거(같은 접근·종류·인용)는 계속 뺀 채로 둔다.
 */
export function mergeApproachEvidence(
  prev: ApproachEvidence[] | undefined,
  next: ApproachEvidence[],
): ApproachEvidence[] {
  const key = (x: { approach: string; kind: string; quote: string }) =>
    `${x.approach}|${x.kind}|${x.quote.replace(/\s+/g, '')}`;
  const excluded = new Set((prev ?? []).filter((x) => x.excluded).map(key));
  return next.map((x) => (excluded.has(key(x)) ? { ...x, excluded: true } : x));
}
